"use client";

import React from "react";
import { RefreshCw, ExternalLink } from "lucide-react";
import { ButtonOutline } from "@/components/common/StandardButton";
import StandardCard from "../common/StandardCard";
import { OrderSet } from "@/types/models/order";

interface OrderSetShippingDetailsProps {
  orderSet?: OrderSet;
  loading?: boolean;
}

const OrderSetShippingDetails: React.FC<OrderSetShippingDetailsProps> = ({
  orderSet,
  loading,
}) => {
  if (loading) {
    return (
      <StandardCard
        title="Shipment Details"
        headerAction={
          <ButtonOutline size="sm" className="flex items-center gap-2" disabled>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </ButtonOutline>
        }
      >
        <div className="space-y-4 animate-pulse">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-24"></div>
              <div className="h-4 bg-muted rounded w-16"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-32"></div>
              <div className="h-4 bg-muted rounded w-20"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-28"></div>
              <div className="h-4 bg-muted rounded w-12"></div>
            </div>
          </div>
        </div>
      </StandardCard>
    );
  }

  if (!orderSet) {
    return (
      <StandardCard
        title="Shipment Details"
        headerAction={
          <ButtonOutline size="sm" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </ButtonOutline>
        }
      >
        <div className="text-center text-muted-foreground">
          No shipment information available
        </div>
      </StandardCard>
    );
  }

  const shippingDetails = orderSet.shipping_details;
  const orderItems = orderSet.items || [];
  const totalQuantity = orderItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const totalWeight = orderItems.reduce(
    (sum, item) => sum + (item.shipping_details?.weight || 0) * item.quantity,
    0,
  );
  const chargeableWeight = totalWeight > 0 ? `${totalWeight}g` : "N/A";

  const deliveryCharges =
    orderSet.charges?.find(
      (charge) =>
        charge.name.toLowerCase().includes("delivery") ||
        charge.name.toLowerCase().includes("shipping"),
    )?.amount || 0;

  return (
    <StandardCard
      title="Shipment Details"
      headerAction={
        <ButtonOutline size="sm" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </ButtonOutline>
      }
    >
      <div className="space-y-4">
        {/* Pickup Address */}
        {orderSet.address && (
          <div>
            <div className="text-sm text-muted-foreground mb-2">
              Pickup Location
            </div>
            <div className="text-sm font-medium text-foreground">
              {orderSet.address.city}, {orderSet.address.state}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {orderSet.address.postal_code}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {/* Delivery Charges */}
          {deliveryCharges > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Delivery Charges
              </span>
              <span className="font-medium text-foreground">
                ₹{(deliveryCharges / 100).toFixed(2)}
              </span>
            </div>
          )}

          {/* Shipment ID */}
          {shippingDetails?.shipment_id && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Shipment ID</span>
              <span className="font-medium text-foreground font-mono text-xs">
                {shippingDetails.shipment_id}
              </span>
            </div>
          )}

          {/* AWB Code */}
          {shippingDetails?.awb_code && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">AWB Code</span>
              <span className="font-medium text-foreground font-mono text-xs">
                {shippingDetails.awb_code}
              </span>
            </div>
          )}

          {/* Courier */}
          {shippingDetails?.courier_name && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Courier</span>
              <span className="font-medium text-foreground">
                {shippingDetails.courier_name}
              </span>
            </div>
          )}

          {/* Tracking URL */}
          {shippingDetails?.tracking_url && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Track Shipment
              </span>
              <a
                href={shippingDetails.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Track <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {/* Total Items */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Items</span>
            <span className="font-medium text-foreground">{totalQuantity}</span>
          </div>

          {/* SKU Count */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">SKU Count</span>
            <span className="font-medium text-foreground">
              {orderItems.length}
            </span>
          </div>

          {/* Status */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Status</span>
            <span className="font-medium text-foreground">
              {orderSet.shipping_status
                ?.replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l: string) => l.toUpperCase()) || "Pending"}
            </span>
          </div>

          {/* Chargeable Weight */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Chargeable Weight
            </span>
            <span className="font-medium text-foreground">
              {chargeableWeight}
            </span>
          </div>

          {/* Tracking ID */}
          {shippingDetails?.tracking_id && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Tracking ID</span>
              <span className="font-medium text-foreground font-mono text-xs">
                {shippingDetails.tracking_id}
              </span>
            </div>
          )}
        </div>

      </div>
    </StandardCard>
  );
};

export default OrderSetShippingDetails;
